import React, { useEffect, useMemo } from 'react'
import { debounce } from 'lodash'
import { SearchLg, FilterLines, Trash01 } from '@untitled-ui/icons-react'
import {
  MyColumn,
  MyDataTable,
  MyModalSlider,
  MyButton,
  MyHorizontalTabV2,
  MyTextField,
  MyChip,
  MyFilterModal,
} from '@interstellar-component'
import { useCompany } from '../Context'
import MyDetailSlider from './MyDetailSlider/DetailSlider'
import TopupPanel from './MyDetailSlider/PurchaseQuotaModal'
import SubscribePanel from './MyDetailSlider/SubscribePanel'
import ResendApplicationPanel from './MyDetailSlider/ResendApplicationPanel'
import InvoicePaymentPanel from './MyDetailSlider/InvoicePaymentPanel'
import MyMemberStatusChip, { AgreementStatusChip, memberStatusOf } from './MyMemberStatusChip'
import AuthImage from '@src/components/AuthImage'

const STATUS_TABS = [
  { label: 'All status', value: 'All status' },
  { label: 'Active', value: 'active' },
  { label: 'Waiting Approval', value: 'waiting_approval' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Expired', value: 'expired' },
]

function CompanyTable() {
  const {
    handleCurrentSlider,
    currentSlider,
    topupPanelOpen,
    closeTopupPanel,
    subscribePanelOpen,
    closeSubscribePanel,
    resendPanelOpen,
    closeResendPanel,
    invoicePanelId,
    closeInvoicePanel,
    closeBillingPanels,
    getCompany,
    setSearchTerm,
    companyRows,
    companyMeta,
    company,
    handleCompanySort,
    handleCompanySelectionChange,
    selectedStatus,
    setSelectedStatus,
    setPage,
    setParams,
    sortField,
    sortOrder,
  } = useCompany()

  const rows = companyRows || []
  const selectedCount = rows.filter((d) => d.checked).length

  const total = Number(companyMeta?.total ?? rows.length) || 0
  const currentPage = Number(companyMeta?.current_page ?? 1) || 1
  const perPage =
    Number(companyMeta?.per_page ?? companyMeta?.limit ?? 10) || 10
  const totalPages = Math.max(1, Number(companyMeta?.total_page ?? 1) || 1)

  const itemCountLabel = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(total)

  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchTerm(value), 800),
    [setSearchTerm]
  )

  useEffect(
    () => () => {
      debouncedSearch.cancel()
    },
    [debouncedSearch]
  )

  const values = {
    data: rows,
    meta: {
      current_page: currentPage,
      per_page: perPage,
      total,
      total_page: totalPages,
      prev_page: currentPage > 1 ? currentPage - 1 : null,
      next_page: currentPage < totalPages ? currentPage + 1 : null,
    },
    loading: company.loading,
    checkedAll: rows.length > 0 && rows.every((d) => d.checked),
  }

  return (
    <>
      <MyModalSlider
        scrim
        open={currentSlider?.current === 'details-slider'}
        element={
          <div className="flex h-full flex-row-reverse">
            <MyDetailSlider />
            {subscribePanelOpen ? (
              <SubscribePanel
                companyId={currentSlider?.id}
                onClose={closeSubscribePanel}
              />
            ) : null}
            {resendPanelOpen ? (
              <ResendApplicationPanel
                companyId={currentSlider?.id}
                onClose={closeResendPanel}
              />
            ) : null}
            {topupPanelOpen ? (
              <TopupPanel
                companyId={currentSlider?.id}
                onClose={closeTopupPanel}
              />
            ) : null}
            {invoicePanelId ? (
              <InvoicePaymentPanel
                // key per-invoice so switching rows remounts the panel and all
                // per-invoice UI state (confirmCancel, copied) resets cleanly.
                key={invoicePanelId}
                companyId={currentSlider?.id}
                invoiceId={invoicePanelId}
                onClose={closeInvoicePanel}
              />
            ) : null}
          </div>
        }
        onClose={() => {
          closeBillingPanels()
          handleCurrentSlider(null)
          // Refresh the table so quota/status changes made in the slider show
          // immediately (no manual page refresh).
          getCompany?.()
        }}
      />

      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-2">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray/200 bg-white shadow-sm">
          <div className="flex flex-col justify-between gap-4 border-b border-gray/200 bg-gray/25 px-6 py-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <h3 className="text-[14px] font-semibold text-gray-900">
                Company List
              </h3>
              <span className="rounded-full border border-gray-blue/200 bg-gray-blue/50 px-2 py-0.5 text-xs font-medium text-gray-blue/700">
                {itemCountLabel} item
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {selectedCount > 0 ? (
                <MyButton
                  color="error"
                  variant="outlined"
                  size="md"
                  type="button"
                >
                  <Trash01 className="h-5 w-5" />
                  <p className="text-sm-semibold">Delete</p>
                </MyButton>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 border-b border-gray-200 p-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="w-full max-w-sm">
              <MyTextField
                id="input-search"
                placeholder="Search for company"
                startAdornment={
                  <SearchLg
                    className="size-5 text-gray-light/600"
                    stroke="currentColor"
                  />
                }
                focusColor="#14378E"
                onChangeForm={(e) => debouncedSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <MyFilterModal
                id="filter-company"
                currentFilters={company?.filter}
                onChange={(filter) => {
                  // MyFilterModal.submit spreads its array state into an
                  // object — coerce back so the query builder still sees an
                  // array (same fix pattern as Workforce).
                  const arr = Array.isArray(filter) ? filter : Object.values(filter || {})
                  setParams((prev) => ({ ...prev, filter: arr, page: 1 }))
                }}
                target={(open, handleClick) => (
                  <MyButton
                    color="gray"
                    size="sm"
                    variant="tertiary"
                    customClassname="text-gray-700"
                    onClick={handleClick}
                  >
                    <FilterLines
                      className="h-4 w-4 text-gray-500"
                      stroke="currentColor"
                    />
                    Filters
                  </MyButton>
                )}
              />
              <MyHorizontalTabV2
                value={selectedStatus}
                onChange={setSelectedStatus}
                fitContent
                tabs={STATUS_TABS}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <MyDataTable
              values={values}
              currentSortFieldFromParams={sortField}
              currentSortOrderFromParams={sortOrder}
              onClick={(row) =>
                handleCurrentSlider({ current: 'details-slider' }, row.id, row)
              }
              paginator
              onChangePagination={setPage}
            >
              <MyColumn
                header="Company name & ID"
                field="name"
                onSort={handleCompanySort}
                body={(row) => (
                  <div className="flex items-center gap-3 whitespace-nowrap py-1">
                    {row.full_logo_url ? (
                      <AuthImage
                        src={row.full_logo_url}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                        <span className="text-sm font-medium">
                          {row.name?.charAt(0) ?? '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-sm font-medium text-brand/700">
                        {row.name}
                      </span>
                      <span
                        className="truncate text-sm text-gray-500"
                        title={row.company_id}
                      >
                        {row.company_id}
                      </span>
                    </div>
                  </div>
                )}
              />
              <MyColumn
                header="Member status"
                field="member_status"
                onSort={handleCompanySort}
                body={(row) => {
                  // "Member status" follows the membership lifecycle (see
                  // memberStatusOf): verification_status once reviewed, else the
                  // onboarding (enrollment) phase.
                  const status = memberStatusOf(row)

                  const memberChip =
                    status === 'in_progress' ? (
                      <MyChip
                        dot={1}
                        label="In progress"
                        color="modern"
                        variant="outlined"
                        size="sm"
                        rounded="md"
                        customDotColor="error/500"
                      />
                    ) : (
                      <MyMemberStatusChip status={status} />
                    )
                  // The membership agreement (DocuSign) state rides along as a
                  // second chip — e.g. "Approved" + "Waiting CLIK signature".
                  return (
                    <div className="flex flex-col gap-1">
                      {memberChip}
                      <AgreementStatusChip company={row} />
                    </div>
                  )
                }}
              />
              <MyColumn
                header="Quota left"
                field="quota_left"
                onSort={handleCompanySort}
                body={(row) => {
                  const total = Number(row.balance?.total_quota || 0)
                  const used = Number(row.balance?.used_quota || 0)
                  // Net out held (reserved/in-flight) quota; prefer the backend's
                  // available_quota, fall back to total-used-held.
                  const held = Number(row.balance?.held_quota || 0)
                  const remaining = row.balance?.available_quota != null
                    ? Math.max(0, Number(row.balance.available_quota))
                    : Math.max(0, total - used - held)
                  return (
                    <span className="text-sm tabular-nums text-gray-600">
                      {remaining.toLocaleString('id-ID')}
                    </span>
                  )
                }}
              />
            </MyDataTable>
          </div>
        </div>
      </div>
    </>
  )
}

export default CompanyTable
